import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Progress,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  BarChartOutlined,
  CheckCircleOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DollarOutlined,
  HeartOutlined,
  LogoutOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getBillingReport, getUsers } from "../../services/billingService";
import { getMonthlyInvoices } from "../../services/usageBillingService";
import { createTicket, getMyTickets } from "../../services/supportService";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);
const fmtUSD  = (n) => `$${parseFloat(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMonth = (m) => {
  if (!m) return "—";
  const [year, month] = m.split("-");
  return new Date(year, month - 1).toLocaleString("default", { month: "short", year: "numeric" });
};

const ACCENT = "#1677ff";

const MENU_ITEMS = [
  { key: "overview",    icon: <DashboardOutlined />,       label: "Overview" },
  { key: "network",     icon: <BarChartOutlined />,        label: "Network Stats" },
  { key: "financial",   icon: <DollarOutlined />,          label: "Financial Summary" },
  { key: "health",      icon: <HeartOutlined />,            label: "System Health" },
  { key: "support",     icon: <CustomerServiceOutlined />, label: "Support" },
  { key: "profile",     icon: <UserOutlined />,            label: "My Profile" },
];

// Module definitions for System Health
const MODULES = [
  { name: "Doctor",          desc: "Patient enrollment, diagnoses, prescriptions" },
  { name: "Patient",         desc: "Profiles, proxy users, teleconsult" },
  { name: "Pharmacy",        desc: "Prescription dispensing, inventory" },
  { name: "Ophthalmology",   desc: "Eye exams, lens prescriptions" },
  { name: "EMS",             desc: "Emergency dispatch, transport logs" },
  { name: "Billing",         desc: "Payor submission, government billing" },
  { name: "Administration",  desc: "Enrollment, PIN issuance, audit logs" },
  { name: "Kiosk",           desc: "24/7 prescription dispensing terminals" },
];

const ROLE_LABELS = {
  DOCTOR: "Doctor", PATIENT: "Patient", PHARMACIST: "Pharmacist",
  OPHTHALMOLOGIST: "Ophthalmologist", OPTICIAN: "Optician",
  EMS_DISPATCHER: "EMS Dispatcher", EMS_DRIVER: "EMS Driver",
  BILLING_STAFF: "Billing Staff", SYSTEM_ADMIN: "System Admin",
  SHAREHOLDER: "Shareholder",
};
const ROLE_COLORS = {
  DOCTOR: "#1677ff", PATIENT: "#52c41a", PHARMACIST: "#722ed1",
  OPHTHALMOLOGIST: "#13c2c2", OPTICIAN: "#2f54eb", EMS_DISPATCHER: "#fa8c16",
  EMS_DRIVER: "#fa541c", BILLING_STAFF: "#eb2f96", SYSTEM_ADMIN: "#595959",
  SHAREHOLDER: "#d48806",
};

export default function ShareholderDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("overview");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  // ── data state ────────────────────────────────────────────────────────────────
  const [allUsers,       setAllUsers]       = useState([]);
  const [invoices,       setInvoices]       = useState([]);
  const [billingReport,  setBillingReport]  = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [usersRes, invRes, billRes] = await Promise.allSettled([
          getUsers(),
          getMonthlyInvoices(),
          getBillingReport(),
        ]);
        if (cancelled) return;
        if (usersRes.status === "fulfilled") setAllUsers(toArray(usersRes.value.data));
        if (invRes.status  === "fulfilled") setInvoices(toArray(invRes.value.data));
        if (billRes.status === "fulfilled") setBillingReport(billRes.value.data ?? null);
      } catch {
        if (!cancelled) setError("Some dashboard data could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // ── derived metrics ───────────────────────────────────────────────────────────
  const roleCounts = allUsers.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] ?? 0) + 1;
    return acc;
  }, {});

  const totalUsers    = allUsers.length;
  const totalDoctors  = roleCounts["DOCTOR"]     ?? 0;
  const totalPatients = roleCounts["PATIENT"]    ?? 0;
  const totalPharmacy = roleCounts["PHARMACIST"] ?? 0;
  const activeUsers   = allUsers.filter(u => u.is_active).length;

  // Invoice aggregates
  const totalInvoiced = invoices.reduce((s, inv) => s + parseFloat(inv.total_amount ?? 0), 0);
  const invByStatus   = invoices.reduce((acc, inv) => {
    acc[inv.status] = (acc[inv.status] ?? 0) + 1;
    return acc;
  }, {});

  // Current month invoice
  const now          = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const thisMonthInv = invoices.find(inv => inv.billing_month === currentMonthKey);

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [{ key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: handleLogout }];

  // ── support ───────────────────────────────────────────────────────────────────
  const [myTickets,      setMyTickets]      = useState([]);
  const [ticketModal,    setTicketModal]    = useState(false);
  const [ticketDetail,   setTicketDetail]   = useState(null);
  const [supportLoaded,  setSupportLoaded]  = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [ticketForm]                        = Form.useForm();

  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const res = await getMyTickets();
      setMyTickets(toArray(res.data).map(r=>({...r,key:r.id})));
      setSupportLoaded(true);
    } catch { /* silent */ } finally { setSupportLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedMenu === "support" && !supportLoaded) loadSupport();
  }, [selectedMenu, supportLoaded, loadSupport]);

  const handleCreateTicket = async (values) => {
    try {
      await createTicket(values);
      Modal.success({ title: "Ticket submitted", content: "Your ticket has been received." });
      setTicketModal(false);
      ticketForm.resetFields();
      loadSupport();
    } catch (err) {
      Modal.error({ title: "Failed", content: err?.response?.data?.detail ?? "Failed to submit ticket." });
    }
  };

  // ── views ──────────────────────────────────────────────────────────────────────
  function renderContent() {
    switch (selectedMenu) {

      // ── OVERVIEW ──────────────────────────────────────────────────────────────
      case "overview":
        return (
          <>
            <Title level={4} style={{ marginBottom: 8, color: "#262626" }}>Executive Overview</Title>
            <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
              Read-only view — last refreshed {new Date().toLocaleTimeString()}
            </Text>

            {/* Key metric cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {[
                { title: "Total Users",       value: totalUsers,    color: ACCENT },
                { title: "Doctors",           value: totalDoctors,  color: "#1677ff" },
                { title: "Patients",          value: totalPatients, color: "#52c41a" },
                { title: "Pharmacies",        value: totalPharmacy, color: "#722ed1" },
                { title: "Active Accounts",   value: activeUsers,   color: "#13c2c2" },
                { title: "Monthly Invoices",  value: invoices.length, color: "#fa8c16" },
              ].map(({ title, value, color }) => (
                <Col xs={24} sm={12} lg={4} key={title}>
                  <Card bordered={false} style={{ borderRadius: 8, textAlign: "center" }}>
                    <Statistic
                      title={<Text style={{ color: "#8c8c8c", fontSize: 12 }}>{title}</Text>}
                      value={value}
                      valueStyle={{ color, fontSize: 26, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            {/* This month snapshot */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} md={12}>
                <Card title={<Text strong>This Month — {fmtMonth(currentMonthKey)}</Text>}
                  bordered={false} style={{ borderRadius: 8 }}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 12 }}>Registrations</Text>}
                        value={thisMonthInv?.total_patient_registrations ?? "—"}
                        valueStyle={{ color: "#52c41a", fontSize: 22, fontWeight: 600 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 12 }}>Prescriptions Issued</Text>}
                        value={thisMonthInv?.total_prescriptions_issued ?? "—"}
                        valueStyle={{ color: "#722ed1", fontSize: 22, fontWeight: 600 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 12 }}>Revenue</Text>}
                        value={thisMonthInv ? fmtUSD(thisMonthInv.total_amount) : "—"}
                        valueStyle={{ color: "#fa8c16", fontSize: 22, fontWeight: 600 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 12 }}>Invoice Status</Text>}
                        value={thisMonthInv?.status ?? "—"}
                        valueStyle={{ color: "#595959", fontSize: 18, fontWeight: 600 }}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card title={<Text strong>Network Composition</Text>}
                  bordered={false} style={{ borderRadius: 8 }}>
                  {["DOCTOR", "PATIENT", "PHARMACIST", "EMS_DISPATCHER", "EMS_DRIVER"].map(role => {
                    const count = roleCounts[role] ?? 0;
                    const pct   = totalUsers > 0 ? Math.round((count / totalUsers) * 100) : 0;
                    return (
                      <div key={role} style={{ marginBottom: 10 }}>
                        <Row justify="space-between" style={{ marginBottom: 2 }}>
                          <Text style={{ fontSize: 12 }}>{ROLE_LABELS[role]}</Text>
                          <Text style={{ fontSize: 12, color: "#8c8c8c" }}>{count}</Text>
                        </Row>
                        <Progress percent={pct} size="small" strokeColor={ROLE_COLORS[role]} showInfo={false} />
                      </div>
                    );
                  })}
                </Card>
              </Col>
            </Row>

            <Alert type="info" showIcon
              message="All data displayed is aggregate. No individual patient information is shown in this view."
              style={{ borderRadius: 8 }} />
          </>
        );

      // ── NETWORK STATS ─────────────────────────────────────────────────────────
      case "network":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>Network Statistics</Title>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} md={12}>
                <Card title={<Text strong>User Distribution by Role</Text>}
                  bordered={false} style={{ borderRadius: 8 }}>
                  {Object.entries(ROLE_LABELS).map(([role, label]) => {
                    const count = roleCounts[role] ?? 0;
                    const pct   = totalUsers > 0 ? Math.round((count / totalUsers) * 100) : 0;
                    return (
                      <div key={role} style={{ marginBottom: 12 }}>
                        <Row justify="space-between" style={{ marginBottom: 4 }}>
                          <Text>{label}</Text>
                          <Text style={{ color: "#8c8c8c" }}>{count} users ({pct}%)</Text>
                        </Row>
                        <Progress
                          percent={pct}
                          strokeColor={ROLE_COLORS[role]}
                          size="small"
                          showInfo={false}
                        />
                      </div>
                    );
                  })}
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card title={<Text strong>Account Status</Text>}
                  bordered={false} style={{ borderRadius: 8 }}>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 12 }}>Active Accounts</Text>}
                        value={activeUsers}
                        valueStyle={{ color: "#52c41a", fontSize: 28, fontWeight: 700 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 12 }}>Suspended Accounts</Text>}
                        value={totalUsers - activeUsers}
                        valueStyle={{ color: "#ff4d4f", fontSize: 28, fontWeight: 700 }}
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 24 }}>
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                      <Text>Active</Text>
                      <Text style={{ color: "#8c8c8c" }}>
                        {totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}%
                      </Text>
                    </Row>
                    <Progress
                      percent={totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}
                      strokeColor="#52c41a"
                      trailColor="#ff4d4f"
                    />
                  </div>
                </Card>

                <Card title={<Text strong>Invoice Status Breakdown</Text>}
                  bordered={false} style={{ borderRadius: 8, marginTop: 16 }}>
                  {Object.entries({ DRAFT: "default", SENT: "blue", PAID: "green", OVERDUE: "red" }).map(([status, color]) => (
                    <Row key={status} justify="space-between" style={{ marginBottom: 8 }}>
                      <Tag color={color}>{status}</Tag>
                      <Text strong>{invByStatus[status] ?? 0} invoice{(invByStatus[status] ?? 0) !== 1 ? "s" : ""}</Text>
                    </Row>
                  ))}
                </Card>
              </Col>
            </Row>
          </>
        );

      // ── FINANCIAL SUMMARY ─────────────────────────────────────────────────────
      case "financial": {
        const report  = billingReport ?? {};
        const byPayor = report.count_by_payor_type ?? {};

        // Monthly revenue from invoices — sorted newest first
        const monthlyRows = [...invoices]
          .sort((a, b) => (b.billing_month ?? "").localeCompare(a.billing_month ?? ""))
          .slice(0, 12);

        const maxAmount = Math.max(...monthlyRows.map(r => parseFloat(r.total_amount ?? 0)), 1);

        return (
          <>
            <Title level={4} style={{ marginBottom: 8, color: "#262626" }}>Financial Summary</Title>
            <Alert type="warning" showIcon style={{ marginBottom: 24, borderRadius: 8 }}
              message="Aggregate data only — no individual patient or provider billing details are shown." />

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Total Platform Revenue</Text>}
                    value={fmtUSD(report.total_billed ?? totalInvoiced)}
                    valueStyle={{ color: "#52c41a", fontSize: 24, fontWeight: 700 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Total Paid</Text>}
                    value={fmtUSD(report.total_paid)}
                    valueStyle={{ color: ACCENT, fontSize: 24, fontWeight: 700 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Total Invoices</Text>}
                    value={invoices.length}
                    valueStyle={{ color: "#fa8c16", fontSize: 24, fontWeight: 700 }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {/* Invoice status */}
              <Col xs={24} md={10}>
                <Card title={<Text strong>Invoice Status</Text>}
                  bordered={false} style={{ borderRadius: 8 }}>
                  {[["PAID", "green", "#52c41a"], ["SENT", "blue", ACCENT], ["DRAFT", "default", "#8c8c8c"], ["OVERDUE", "red", "#ff4d4f"]].map(([status, tagColor, barColor]) => {
                    const count = invByStatus[status] ?? 0;
                    const pct   = invoices.length > 0 ? Math.round((count / invoices.length) * 100) : 0;
                    return (
                      <div key={status} style={{ marginBottom: 14 }}>
                        <Row justify="space-between" style={{ marginBottom: 4 }}>
                          <Tag color={tagColor}>{status}</Tag>
                          <Text style={{ color: "#8c8c8c" }}>{count} ({pct}%)</Text>
                        </Row>
                        <Progress percent={pct} strokeColor={barColor} size="small" showInfo={false} />
                      </div>
                    );
                  })}
                </Card>

                {/* Payor breakdown */}
                {Object.keys(byPayor).length > 0 && (
                  <Card title={<Text strong>Billing by Payor Type</Text>}
                    bordered={false} style={{ borderRadius: 8, marginTop: 16 }}>
                    {Object.entries(byPayor).map(([payor, count]) => (
                      <Row key={payor} justify="space-between" style={{ marginBottom: 6 }}>
                        <Tag>{payor}</Tag>
                        <Text strong>{count} record{count !== 1 ? "s" : ""}</Text>
                      </Row>
                    ))}
                  </Card>
                )}
              </Col>

              {/* Monthly revenue trend */}
              <Col xs={24} md={14}>
                <Card title={<Text strong>Monthly Revenue Trend (Last 12 Months)</Text>}
                  bordered={false} style={{ borderRadius: 8 }}>
                  {monthlyRows.length === 0 ? (
                    <Text type="secondary">No invoice data available.</Text>
                  ) : (
                    monthlyRows.map(row => {
                      const amount = parseFloat(row.total_amount ?? 0);
                      const pct    = Math.round((amount / maxAmount) * 100);
                      return (
                        <div key={row.billing_month} style={{ marginBottom: 12 }}>
                          <Row justify="space-between" style={{ marginBottom: 3 }}>
                            <Text style={{ fontSize: 13 }}>{fmtMonth(row.billing_month)}</Text>
                            <Row gutter={8} align="middle">
                              <Col><Tag color={{ PAID: "green", SENT: "blue", DRAFT: "default", OVERDUE: "red" }[row.status] ?? "default"}>{row.status}</Tag></Col>
                              <Col><Text strong style={{ fontSize: 13 }}>{fmtUSD(amount)}</Text></Col>
                            </Row>
                          </Row>
                          <Progress percent={pct} strokeColor="#1677ff" size="small" showInfo={false} />
                        </div>
                      );
                    })
                  )}
                </Card>
              </Col>
            </Row>
          </>
        );
      }

      // ── SYSTEM HEALTH ─────────────────────────────────────────────────────────
      case "health":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>System Health</Title>

            {/* Overall status banner */}
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={<Text strong style={{ fontSize: 15 }}>All Systems Operational</Text>}
              description="All MCHUSA DHN modules are running normally."
              style={{ marginBottom: 24, borderRadius: 8 }}
            />

            {/* Module status cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {MODULES.map(({ name, desc }) => (
                <Col xs={24} sm={12} lg={6} key={name}>
                  <Card bordered={false} style={{ borderRadius: 8 }}>
                    <Row align="middle" gutter={10} style={{ marginBottom: 6 }}>
                      <Col><Badge status="success" /></Col>
                      <Col><Text strong>{name}</Text></Col>
                    </Row>
                    <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>
                    <div style={{ marginTop: 10 }}>
                      <Tag color="green" style={{ fontSize: 11 }}>Operational</Tag>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Platform stats */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Registered Modules</Text>}
                    value={MODULES.length}
                    suffix="/ 8"
                    valueStyle={{ color: "#52c41a", fontSize: 28, fontWeight: 700 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Active User Accounts</Text>}
                    value={activeUsers}
                    valueStyle={{ color: ACCENT, fontSize: 28, fontWeight: 700 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Total Invoices Generated</Text>}
                    value={invoices.length}
                    valueStyle={{ color: "#fa8c16", fontSize: 28, fontWeight: 700 }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        );

      case "support":
        return (
          <Spin spinning={supportLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Support</Title>
            <Card bordered={false} style={{ borderRadius: 8 }} title={<Text strong>My Tickets</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setTicketModal(true)}>Report an Issue</Button>}>
              <Table size="small" dataSource={myTickets} pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "No tickets yet" }}
                columns={[
                  { title:"ID",       dataIndex:"id",       key:"id",       width:90, render:id=><Text code style={{fontSize:11}}>{String(id).slice(0,8)}</Text> },
                  { title:"Subject",  dataIndex:"subject",  key:"subject",  ellipsis:true },
                  { title:"Priority", dataIndex:"priority", key:"priority", width:100, render:p=><Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[p]??"default"}>{p}</Tag> },
                  { title:"Status",   dataIndex:"status",   key:"status",   width:110, render:s=><Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default"}[s]??"default"}>{s?.replace(/_/g," ")}</Tag> },
                  { title:"Created",  dataIndex:"created_at",key:"created_at",width:110, render:d=>d?new Date(d).toLocaleDateString():"—" },
                  { title:"Actions",  key:"actions",        width:80, render:(_,r)=><Button size="small" onClick={()=>setTicketDetail(r)}>View</Button> },
                ]}
              />
            </Card>
          </Spin>
        );

      case "profile":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Profile</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Email">{user?.email ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Role"><Tag color="gold">SHAREHOLDER</Tag></Descriptions.Item>
              </Descriptions>
            </Card>
            <ChangePasswordForm />
          </>
        );

      default: return null;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}>
        <div style={{
          height: 64, display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid #f0f0f0", padding: "0 16px",
        }}>
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
              <Text strong style={{ color: "#d48806", fontSize: 13, whiteSpace: "nowrap" }}>Digital Healthcare Network</Text>
              <Text style={{ color: "#d48806", fontSize: 12, whiteSpace: "nowrap" }}>Shareholder Portal</Text>
            </div>
          )}
        </div>
        <Menu mode="inline" selectedKeys={[selectedMenu]} items={MENU_ITEMS}
          onClick={({ key }) => setSelectedMenu(key)}
          style={{ border: "none", marginTop: 8 }} />
      </Sider>

      <Layout>
        <Header style={{
          background: "#fff", padding: "0 24px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: "1px solid #f0f0f0",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div>
            <Text strong style={{ fontSize: 16, color: "#1677ff" }}>MCHUSA</Text>
            <Text style={{ fontSize: 14, color: "#8c8c8c", marginLeft: 8 }}>| Shareholder Portal</Text>
            <Tag color="gold" style={{ marginLeft: 12, fontSize: 11 }}>READ ONLY</Tag>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <Avatar size={32} icon={<UserOutlined />} style={{ background: "#d48806" }} />
              <Text style={{ fontSize: 14, color: "#262626" }}>{user.email ?? "Shareholder"}</Text>
            </div>
          </Dropdown>
        </Header>

        <Content style={{ padding: 24, background: "#f5f7fa" }}>
          {error && (
            <Alert type="warning" message={error} showIcon closable
              style={{ marginBottom: 24, borderRadius: 8 }} onClose={() => setError(null)} />
          )}
          <Spin spinning={loading} tip="Loading dashboard…">
            {renderContent()}
          </Spin>
        </Content>
      </Layout>

      {/* ── New Ticket Modal ── */}
      <Modal title="Report an Issue" open={ticketModal}
        onOk={()=>ticketForm.submit()} onCancel={()=>{ setTicketModal(false); ticketForm.resetFields(); }}
        okText="Submit" destroyOnClose maskClosable keyboard>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}>
          <Form.Item name="subject" label="Subject" rules={[{required:true,message:"Required"}]}>
            <Input placeholder="Brief description of the issue" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{required:true,message:"Select priority"}]}>
            <Select placeholder="Select priority">
              <Select.Option value="LOW">Low</Select.Option>
              <Select.Option value="MEDIUM">Medium</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="CRITICAL">Critical</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{required:true,message:"Required"}]}>
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={()=>setTicketDetail(null)}
        footer={<Button onClick={()=>setTicketDetail(null)}>Close</Button>}
        destroyOnClose maskClosable keyboard width={500}>
        {ticketDetail && (
          <div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <tbody>
                {[
                  ["Subject",  ticketDetail.subject],
                  ["Priority", <Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",   <Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default"}[ticketDetail.status]}>{ticketDetail.status?.replace(/_/g," ")}</Tag>],
                  ["Created",  new Date(ticketDetail.created_at).toLocaleString()],
                  ["Updated",  new Date(ticketDetail.updated_at).toLocaleString()],
                ].map(([label,val])=>(
                  <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                    <td style={{padding:"8px 12px",color:"#8c8c8c",width:"35%"}}>{label}</td>
                    <td style={{padding:"8px 12px"}}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:12}}>
              <Text strong>Description</Text>
              <div style={{marginTop:6,padding:10,background:"#fafafa",borderRadius:4,fontSize:13,color:"#595959"}}>{ticketDetail.description}</div>
            </div>
            {ticketDetail.resolution_notes && (
              <div style={{marginTop:12}}>
                <Text strong>Resolution Notes</Text>
                <div style={{marginTop:6,padding:10,background:"#f6ffed",border:"1px solid #b7eb8f",borderRadius:4,fontSize:13}}>{ticketDetail.resolution_notes}</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}
